import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

function normalizeMathExpression(input) {
  return String(input || "")
    .replace(/[xX×]/g, "*")
    .replace(/[\[\{]/g, "(")
    .replace(/[\]\}]/g, ")");
}

function tokenizeMathExpression(input) {
  const tokens = [];
  const src = normalizeMathExpression(input);
  let i = 0;

  while (i < src.length) {
    const ch = src[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if ("+-*/()%".includes(ch)) {
      tokens.push({ type: ch, value: ch });
      i += 1;
      continue;
    }

    if (/[0-9.]/.test(ch)) {
      let j = i;
      let dotCount = 0;
      while (j < src.length && /[0-9.]/.test(src[j])) {
        if (src[j] === ".") dotCount += 1;
        if (dotCount > 1) return null;
        j += 1;
      }
      const numStr = src.slice(i, j);
      if (numStr === ".") return null;
      const num = Number(numStr);
      if (!Number.isFinite(num)) return null;
      tokens.push({ type: "number", value: num });
      i = j;
      continue;
    }

    return null;
  }

  return tokens;
}

export function evaluateMathExpression(expressionText) {
  const tokens = tokenizeMathExpression(expressionText);
  if (!tokens || tokens.length === 0) return null;

  let idx = 0;
  const peek = () => tokens[idx] || null;
  const consume = (type) => {
    const t = peek();
    if (t && t.type === type) {
      idx += 1;
      return t;
    }
    return null;
  };

  const parseExpression = () => {
    let value = parseTerm();
    if (value === null) return null;
    while (true) {
      if (consume("+")) {
        const rhs = parseTerm();
        if (rhs === null) return null;
        value += rhs;
        continue;
      }
      if (consume("-")) {
        const rhs = parseTerm();
        if (rhs === null) return null;
        value -= rhs;
        continue;
      }
      break;
    }
    return value;
  };

  const parseTerm = () => {
    let value = parseUnary();
    if (value === null) return null;
    while (true) {
      if (consume("*")) {
        const rhs = parseUnary();
        if (rhs === null) return null;
        value *= rhs;
        continue;
      }
      if (consume("/")) {
        const rhs = parseUnary();
        if (rhs === null || rhs === 0) return null;
        value /= rhs;
        continue;
      }
      break;
    }
    return value;
  };

  const parseUnary = () => {
    if (consume("+")) {
      return parseUnary();
    }
    if (consume("-")) {
      const v = parseUnary();
      return v === null ? null : -v;
    }
    return parsePostfix();
  };

  const parsePostfix = () => {
    let value = parsePrimary();
    if (value === null) return null;
    while (consume("%")) {
      value /= 100;
    }
    return value;
  };

  const parsePrimary = () => {
    const n = consume("number");
    if (n) return n.value;

    if (consume("(")) {
      const inner = parseExpression();
      if (inner === null || !consume(")")) return null;
      return inner;
    }
    return null;
  };

  const result = parseExpression();
  if (result === null || idx !== tokens.length || !Number.isFinite(result)) {
    return null;
  }
  return result;
}

function formatMathResult(value, decimals) {
  const rounded = Number(Number(value).toFixed(decimals));
  if (!Number.isFinite(rounded)) return null;
  return String(rounded);
}

function hasMathOperators(expr) {
  return /[+\-*/xX%()]/.test(String(expr || ""));
}

function getExpressionBeforeTrailingEquals(raw, eqIndex) {
  const left = String(raw || "").slice(0, eqIndex).trimEnd();
  const match = left.match(/([0-9+\-*/xX%().\[\]{}\s]+)$/);
  if (!match) return null;
  const expr = String(match[1] || "").trim();
  if (!expr || !hasMathOperators(expr)) return null;
  return expr;
}

function getExpressionAroundLastEquals(rawText) {
  const raw = String(rawText ?? "");
  const eqIndex = raw.lastIndexOf("=");
  if (eqIndex < 0) return null;

  const afterEq = raw.slice(eqIndex + 1).trim();
  if (afterEq) {
    if (!hasMathOperators(afterEq)) return null;
    return { raw, eqIndex, expr: afterEq, trailingEquals: false };
  }

  const beforeEqExpr = getExpressionBeforeTrailingEquals(raw, eqIndex);
  if (!beforeEqExpr) return null;
  return { raw, eqIndex, expr: beforeEqExpr, trailingEquals: true };
}

export function getSolvedNoteText(rawText, decimals = 6) {
  const parsed = getExpressionAroundLastEquals(rawText);
  if (!parsed) return null;

  const { raw, eqIndex, expr, trailingEquals } = parsed;

  const result = evaluateMathExpression(expr);
  if (result === null) return null;

  const formatted = formatMathResult(result, decimals);
  if (formatted == null) return null;

  if (trailingEquals) {
    const prefix = raw.slice(0, eqIndex + 1);
    const spacer = prefix.endsWith(" ") ? "" : " ";
    return `${prefix}${spacer}${formatted}`;
  }

  const spacer = raw.endsWith(" ") ? "" : " ";
  return `${raw}${spacer}= ${formatted}`;
}

export function getSolvedAmountText(rawValue, decimals = 2) {
  const parsed = getExpressionAroundLastEquals(rawValue);
  if (!parsed) return null;

  const { expr } = parsed;
  const result = evaluateMathExpression(expr);
  if (result === null) return null;

  const formatted = formatMathResult(result, decimals);
  return formatted == null ? null : formatted;
}

export function solveMathInNoteText(rawText, decimals = 6) {
  return getSolvedNoteText(rawText, decimals) ?? String(rawText ?? "");
}

export function solveMathForNumericInput(rawValue, decimals = 2) {
  return getSolvedAmountText(rawValue, decimals) ?? String(rawValue ?? "");
}
